type PageSize = {
  width?: number
  height?: number
}

type ShufflePage = {
  pageProperties?: {
    pageNumber?: number
    product?: string
    pageSize?: PageSize
  }
}

const sizesDiffer = (a?: PageSize, b?: PageSize): boolean => {
  if (!a || !b) return false
  const widthDiff = a.width != null && b.width != null && a.width !== b.width
  const heightDiff =
    a.height != null && b.height != null && a.height !== b.height
  return widthDiff || heightDiff
}

/**
 * Detects the locked guestbook cover page — page 1 of a guestbook-style layout
 * whose cover is a different physical size than the interior pages.
 *
 * Mirrors the backend split signal in
 * apps/mdl-backend/src/services/pdf.service.ts (shouldSplitPDFByPageSize): the
 * cover is split off into its own PDF because its size differs from the request
 * page size, so it must stay at position 1. Reordering it corrupts the split
 * PDF. All other pages remain freely reorderable.
 *
 * Detection is data-driven and app-agnostic (the product owns the page shape,
 * not the caller): the cover is page 1 whose pageSize differs from the interior
 * pages, OR — for legacy rows missing/uniform pageSize — a Register/Guest Book
 * product name.
 *
 * @param page - The page to test
 * @param allPages - The full ordered page set
 * @returns true only for the locked guestbook cover page
 */
export const isGuestbookCoverPage = (
  page: ShufflePage | undefined,
  allPages: ShufflePage[] | undefined,
): boolean => {
  if (!allPages || allPages.length < 2) return false
  if (page?.pageProperties?.pageNumber !== 1) return false

  const interiorPage = allPages.find((p) => p?.pageProperties?.pageNumber === 2)

  const differentSize = sizesDiffer(
    page?.pageProperties?.pageSize,
    interiorPage?.pageProperties?.pageSize,
  )

  const nameSignal = /register book|guest ?book/i.test(
    page?.pageProperties?.product || '',
  )

  return differentSize || nameSignal
}

export default isGuestbookCoverPage
